import {
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    IconButton,
    InputAdornment, InputLabel, Menu, MenuItem,
    Paper, Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {Add, Close as CloseIcon} from "@mui/icons-material";
import React from "react";

export default function ButtonsSection(
    {
        quickReplies,
        setQuickReplies,
        ctas,
        setCtas,
        totalButtons,
        MAX_BUTTONS,
        urlCount,
        phoneCount,
        MAX_URL_CTAS,
        MAX_PHONE_CTAS,
        urlIsValid,
        phoneIsValid,
        openCtaMenu,
        closeCtaMenu,
        ctaMenuEl,
    }
) {
    const canAddAnyButton = totalButtons < MAX_BUTTONS;
    const canAddQuick = quickReplies.length < 10;
    const canAddUrl = canAddAnyButton && urlCount < MAX_URL_CTAS;
    const canAddPhone = canAddAnyButton && phoneCount < MAX_PHONE_CTAS;

    const addQuickReplyRow = () => {
        if (!canAddQuick || !canAddAnyButton) return;
        setQuickReplies((prev) => [...prev, ""]);
    };
    const updateQuickAt = (idx, val) =>
        setQuickReplies((prev) => prev.map((q, i) => (i === idx ? val : q)));
    const removeQuickAt = (idx) =>
        setQuickReplies((prev) => prev.filter((_, i) => i !== idx));

    const addUrlCTA = () => {
        if (!canAddUrl) return;
        setCtas((prev) => [
            ...prev,
            {type: "url", title: "", urlType: "static", value: "", sampleVar: ""},
        ]);
        closeCtaMenu();
    };
    const addPhoneCTA = () => {
        if (!canAddPhone) return;
        setCtas((prev) => [...prev, {type: "phone", title: "", value: ""}]);
        closeCtaMenu();
    };

    const updateCTA = (idx, patch) =>
        setCtas((prev) => prev.map((c, i) => (i === idx ? {...c, ...patch} : c)));
    const removeCTA = (idx) =>
        setCtas((prev) => prev.filter((_, i) => i !== idx));

    return (
        <Paper variant="outlined" sx={{p: 2, borderRadius: 3}}>
            <Stack gap={2}>
                <Stack direction="row" alignItems="center" gap={1} justifyContent="space-between">
                    <Typography variant="h6">Buttons</Typography>
                    <Chip size="small" label={`Total: ${totalButtons}/${MAX_BUTTONS}`}/>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Puedes elegir hasta 10 botones en total.
                </Typography>

                {/* Quick Replies */}
                <Stack gap={1}>
                    {quickReplies.length > 0 && (
                        <Typography variant="subtitle2">Quick Reply</Typography>
                    )}

                    {quickReplies.map((q, idx) => (
                        <TextField
                            key={`qr-${idx}`}
                            value={q}
                            onChange={(e) => updateQuickAt(idx, e.target.value)}
                            placeholder="Título de respuesta rápida"
                            fullWidth
                            size="small"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="remove quick reply"
                                            onClick={() => removeQuickAt(idx)}
                                        >
                                            <CloseIcon/>
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    ))}

                    <Button
                        onClick={addQuickReplyRow}
                        variant="outlined"
                        startIcon={<Add/>}
                        disabled={quickReplies.length >= 10 || !canAddAnyButton}
                        sx={{justifyContent: "center"}}
                    >
                        Quick Reply
                    </Button>
                </Stack>

                <Divider/>

                {/* CTAs */}
                <Stack gap={1}>
                    <Typography variant="subtitle2">Call to action</Typography>

                    {ctas.map((c, idx) => (
                        <Stack key={`cta-${idx}`} gap={1}>
                            <Stack direction={{xs: "column", sm: "row"}} gap={1} alignItems="stretch">
                                <FormControl sx={{minWidth: 180}}>
                                    <InputLabel id={`cta-type-${idx}`}>Select type</InputLabel>
                                    <Select
                                        labelId={`cta-type-${idx}`}
                                        label="Select type"
                                        value={c.type}
                                        onChange={(e) => {
                                            const to = e.target.value;
                                            if (to === "url" && urlCount >= MAX_URL_CTAS && c.type !== "url") return;
                                            if (to === "phone" && phoneCount >= MAX_PHONE_CTAS && c.type !== "phone") return;
                                            updateCTA(
                                                idx,
                                                to === "url"
                                                    ? {
                                                        type: "url",
                                                        urlType: c.urlType || "static",
                                                        value: c.value || "",
                                                        sampleVar: c.sampleVar || "",
                                                    }
                                                    : {type: "phone", urlType: undefined}
                                            );
                                        }}
                                    >
                                        <MenuItem value="url" disabled={c.type !== "url" && urlCount >= MAX_URL_CTAS}>
                                            URL
                                        </MenuItem>
                                        <MenuItem value="phone"
                                                  disabled={c.type !== "phone" && phoneCount >= MAX_PHONE_CTAS}>
                                            Phone Number
                                        </MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    label="Título del botón"
                                    value={c.title}
                                    onChange={(e) => updateCTA(idx, {title: e.target.value})}
                                    fullWidth
                                    size="small"
                                />

                                {c.type === "url" && (
                                    <>
                                        <FormControl sx={{minWidth: 180}}>
                                            <InputLabel id={`url-type-${idx}`}>Select URL type</InputLabel>
                                            <Select
                                                labelId={`url-type-${idx}`}
                                                label="Select URL type"
                                                value={c.urlType || "static"}
                                                onChange={(e) => updateCTA(idx, {urlType: e.target.value})}
                                            >
                                                <MenuItem value="static">Static</MenuItem>
                                                <MenuItem value="dynamic">Dynamic</MenuItem>
                                            </Select>
                                        </FormControl>

                                        <TextField
                                            label="Valor del botón"
                                            placeholder="https://example.com"
                                            value={c.value}
                                            onChange={(e) => updateCTA(idx, {value: e.target.value})}
                                            error={Boolean(c.value) && !/^https?:\/\//i.test(c.value)}
                                            helperText={Boolean(c.value) && !/^https?:\/\//i.test(c.value) ? "URL inválida" : " "}
                                            fullWidth
                                            size="small"
                                        />
                                    </>
                                )}

                                {c.type === "phone" && (
                                    <TextField
                                        label="Valor del botón"
                                        placeholder="+123456789"
                                        value={c.value}
                                        onChange={(e) => updateCTA(idx, {value: e.target.value})}
                                        error={Boolean(c.value) && !/^\+?[0-9\s\-()]{6,}$/.test(c.value)}
                                        helperText={Boolean(c.value) && !/^\+?[0-9\s\-()]{6,}$/.test(c.value) ? "Teléfono inválido" : " "}
                                        fullWidth
                                        size="small"
                                    />
                                )}

                                <IconButton aria-label="remove CTA" onClick={() => removeCTA(idx)}>
                                    <CloseIcon/>
                                </IconButton>
                            </Stack>

                            {/* Campo adicional para URL dinámica — valor de ejemplo para {{1}} */}
                            {c.type === "url" && c.urlType === "dynamic" && (
                                <Box sx={{pl: {sm: 0}, mt: 0.5}}>
                                    <Typography color="error" variant="subtitle2" sx={{mb: 0.5}}>
                                        Sample dynamic URL with variable
                                    </Typography>
                                    <Typography color="error" variant="caption" sx={{mb: 1, display: "block"}}>
                                        El sistema agregar\u00E1 autom\u00E1ticamente {'{{1}}'} al final de la URL. Ingresa el valor de ejemplo para la variable.
                                    </Typography>
                                    <TextField
                                        label="Valor de ejemplo para la variable"
                                        placeholder="/ejemplo"
                                        value={c.sampleVar || ""}
                                        onChange={(e) => updateCTA(idx, {sampleVar: e.target.value})}
                                        fullWidth
                                        size="small"
                                        InputProps={{
                                            startAdornment: <Chip label={'{{1}}'} size="small" sx={{mr: 1}}/>,
                                        }}
                                    />
                                </Box>
                            )}

                            <Divider/>
                        </Stack>
                    ))}

                    <Box>
                        <Button
                            variant="outlined"
                            startIcon={<Add/>}
                            onClick={openCtaMenu}
                            disabled={!canAddAnyButton || (!canAddUrl && !canAddPhone)}
                        >
                            Add CTA
                        </Button>
                        <Menu anchorEl={ctaMenuEl} open={Boolean(ctaMenuEl)} onClose={closeCtaMenu}>
                            <MenuItem onClick={addUrlCTA} disabled={!canAddUrl}>
                                URL
                            </MenuItem>
                            <MenuItem onClick={addPhoneCTA} disabled={!canAddPhone}>
                                Phone Number
                            </MenuItem>
                        </Menu>
                        <Stack direction="row" gap={1} sx={{mt: 1}}>
                            <Chip size="small" label={`URL: ${urlCount}/${MAX_URL_CTAS}`}/>
                            <Chip size="small" label={`Phone: ${phoneCount}/${MAX_PHONE_CTAS}`}/>
                        </Stack>
                    </Box>
                </Stack>
            </Stack>
        </Paper>
    );
}